import React, { memo, useState, useEffect } from 'react';
import { Row, Badge, Spinner, Col, Table, Button } from 'react-bootstrap';
import './sticky-table-header.css';
import { getStillageTrackerLogs } from 'services/stillageDashboard';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import GoogleMapReact from 'google-map-react';
import { mapApiKey } from 'services/stillageDashboard';
import { MapIcon } from '../../stillage-map/MapIcon';

const TrackerLogs = ({ showModal, trackerId }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(null);

    useEffect(() => {
        if (!showModal || !trackerId) {
            setData([]);
            setLoading(false);
            return;
        }

        // Captured locally so a superseded request can tell it is no longer the current one
        const controller = new AbortController();
        setLoading(true);

        getStillageTrackerLogs(trackerId, page, controller)
            .then(res => {
                if (controller.signal.aborted) return;
                setData(res?.logs ?? []);
                setTotalPages(res?.total ?? null);
            })
            .catch(error => {
                if (controller.signal.aborted) return;
                setData([]);
                setTotalPages(null);
                toast.error(`Error fetching tracker logs: ${error.message}`);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [showModal, trackerId, page]);

    // Reset to first page when modal opens or tracker changes
    useEffect(() => {
        if (showModal) setPage(1);
    }, [showModal, trackerId]);

    if (loading)
        return (
            <div className='justify-content-center d-flex w-100 my-5'>
                <Spinner animation="border" role="status" variant='primary' />
            </div>
        );

    if (!data?.length && !loading)
        return (
            <div className='h-100 text-center align-middle my-4'>No data</div>
        );

    return (
        <>
            <Row className='px-3'>
                <Col className='sticky-container'>
                    <div className='sticky-inner'>
                        <Table hover size='sm' bordered className='table-sm sticky-table'>
                            <thead>
                                <tr>
                                    <th className='bg-light'>#</th>
                                    <th className='bg-light'>Device ID</th>
                                    {/* <th className='bg-light'>From Number</th> */}
                                    <th className='bg-light'>Ping Sent</th>
                                    <th className='bg-light'>Lat</th>
                                    <th className='bg-light'>Long</th>
                                    <th className='bg-light'>Speed</th>
                                    <th className='bg-light'>Tracked</th>
                                    <th className='bg-light'>Battery</th>
                                    {/* <th className='bg-light'>Last Known</th> */}
                                    <th className='bg-light'>Map</th>
                                    <th className='bg-light'>Note</th>
                                    <th className='bg-light'>Stillage No.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((item, idx) => {
                                    const batteryLevel = +item?.battery?.replaceAll('%', '') ?? 0;
                                    let batteryClass = 'success';
                                    if (batteryLevel == 0) batteryClass = 'danger';
                                    else if (batteryLevel < 50) batteryClass = 'warning';

                                    return (
                                        <tr key={idx}>
                                            <td>{(page - 1) * 15 + idx + 1}</td>
                                            <td>{item.id}</td>
                                            {/* <td>{item.fromNumber}</td> */}
                                            <td>{dayjs(item.received).format('DD/MM/YYYY HH:mm:ss')}</td>
                                            <td>{item.lat}</td>
                                            <td>{item.lon}</td>
                                            <td>{item.speed}</td>
                                            <td>{dayjs(item.tracked).format('DD/MM/YYYY HH:mm:ss')}</td>
                                            <td><Badge bg={batteryClass}>{batteryLevel}%</Badge></td>
                                            {/* <td>{dayjs(item.lastKnown).format('DD/MM/YYYY HH:mm:ss')}</td> */}
                                            <td>
                                                <div style={{ height: '200px', width: '200px' }}>
                                                    <GoogleMapReact
                                                        bootstrapURLKeys={{ key: mapApiKey }}
                                                        defaultCenter={{ lat: item.lat, lng: item.lon }}
                                                        defaultZoom={12}
                                                    ><MapIcon lng={item.lon} lat={item.lat} />
                                                    </GoogleMapReact>
                                                </div>
                                            </td>
                                            <td>{item.note}</td>
                                            <td><Badge variant="primary">{item.stillageNo}</Badge></td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                    </div>
                </Col>
            </Row >
            <Row className='px-3 py-2'>
                <Col className='d-flex justify-content-between align-items-center'>
                    <div>
                        <Button size='sm' variant='outline-primary' onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className='me-2'>Prev</Button>
                        <Button size='sm' variant='outline-primary' onClick={() => setPage(p => p + 1)} disabled={totalPages ? page >= totalPages : (data.length === 0)} className='me-2'>Next</Button>
                    </div>
                    <div>
                        <small className='text-muted'>Page {page}{totalPages ? ` of ${totalPages}` : ''}</small>
                    </div>
                </Col>
            </Row>
        </>
    );
}

TrackerLogs.propTypes = {
    showModal: PropTypes.bool,
    trackerId: PropTypes.string
};

export default memo(TrackerLogs);